(() => {
  const sectionSelector = '[data-sponsoring-partnerships]';
  const trackSelector = '[data-sponsoring-partnerships-track]';
  const thumbSelector = '[data-sponsoring-partnerships-scrollbar-thumb]';
  const initialized = 'sponsoringPartnershipsInitialized';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initSection = (section) => {
    if (!section || section.dataset[initialized] === 'true') return;

    const track = section.querySelector(trackSelector);
    const thumb = section.querySelector(thumbSelector);
    const progress = thumb?.parentElement;
    const cards = track ? Array.from(track.children) : [];
    if (!track || !progress || !cards.length) return;

    section.dataset[initialized] = 'true';

    let frame = 0;
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let isDragging = false;
    let didDrag = false;

    const firstCardOffset = cards[0].offsetLeft;
    const closestCardIndex = () => cards.reduce((closestIndex, card, index) => (
      Math.abs((card.offsetLeft - firstCardOffset) - track.scrollLeft) < Math.abs((cards[closestIndex].offsetLeft - firstCardOffset) - track.scrollLeft)
        ? index
        : closestIndex
    ), 0);

    const scrollToCard = (index) => {
      const targetIndex = Math.min(Math.max(index, 0), cards.length - 1);
      track.scrollTo({
        left: cards[targetIndex].offsetLeft - firstCardOffset,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    };

    const updateProgress = () => {
      frame = 0;
      const maximumScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
      const hasOverflow = maximumScroll > 1;

      section.classList.toggle('has-overflow', hasOverflow);
      track.tabIndex = hasOverflow ? 0 : -1;
      progress.hidden = !hasOverflow;
      if (!hasOverflow) return;

      window.FWNSliders?.setPosition(progress, track.scrollLeft, maximumScroll);
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateProgress);
    };

    const handleKeydown = (event) => {
      if (!section.classList.contains('has-overflow')) return;
      const currentIndex = closestCardIndex();
      let nextIndex = null;

      if (event.key === 'ArrowRight') nextIndex = currentIndex + 1;
      if (event.key === 'ArrowLeft') nextIndex = currentIndex - 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = cards.length - 1;
      if (nextIndex === null) return;

      event.preventDefault();
      scrollToCard(nextIndex);
    };

    const handlePointerDown = (event) => {
      if (!section.classList.contains('has-overflow') || event.button !== 0 || pointerId !== null) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = track.scrollLeft;
      isDragging = false;
      didDrag = false;
    };

    const handlePointerMove = (event) => {
      if (event.pointerId !== pointerId) return;
      const distanceX = event.clientX - startX;
      const distanceY = event.clientY - startY;

      if (!isDragging) {
        if (Math.abs(distanceX) < 5 && Math.abs(distanceY) < 5) return;
        if (Math.abs(distanceY) > Math.abs(distanceX)) {
          pointerId = null;
          return;
        }

        isDragging = true;
        didDrag = true;
        track.classList.add('is-dragging');
        track.setPointerCapture?.(event.pointerId);
      }

      if (event.cancelable) event.preventDefault();
      track.scrollLeft = startScrollLeft - distanceX;
    };

    const finishPointer = (event) => {
      if (event.pointerId !== pointerId) return;
      if (track.hasPointerCapture?.(event.pointerId)) track.releasePointerCapture(event.pointerId);
      track.classList.remove('is-dragging');
      pointerId = null;
      isDragging = false;
    };

    const preventDraggedClick = (event) => {
      if (!didDrag) return;
      event.preventDefault();
      event.stopPropagation();
      didDrag = false;
    };

    const preventNativeDrag = (event) => event.preventDefault();

    track.addEventListener('keydown', handleKeydown);
    track.addEventListener('pointerdown', handlePointerDown);
    track.addEventListener('pointermove', handlePointerMove);
    track.addEventListener('pointerup', finishPointer);
    track.addEventListener('pointercancel', finishPointer);
    track.addEventListener('click', preventDraggedClick, true);
    track.addEventListener('dragstart', preventNativeDrag);
    track.addEventListener('scroll', scheduleUpdate, { passive: true });

    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(updateProgress) : null;
    resizeObserver?.observe(track);
    resizeObserver?.observe(progress);
    if (!resizeObserver) window.addEventListener('resize', updateProgress, { passive: true });

    section.sponsoringPartnershipsCleanup = () => {
      if (frame) window.cancelAnimationFrame(frame);
      track.removeEventListener('keydown', handleKeydown);
      track.removeEventListener('pointerdown', handlePointerDown);
      track.removeEventListener('pointermove', handlePointerMove);
      track.removeEventListener('pointerup', finishPointer);
      track.removeEventListener('pointercancel', finishPointer);
      track.removeEventListener('click', preventDraggedClick, true);
      track.removeEventListener('dragstart', preventNativeDrag);
      track.removeEventListener('scroll', scheduleUpdate);
      resizeObserver?.disconnect();
      if (!resizeObserver) window.removeEventListener('resize', updateProgress);
      delete section.dataset[initialized];
    };

    updateProgress();
  };

  const init = (root = document) => {
    if (root.matches?.(sectionSelector)) initSection(root);
    root.querySelectorAll?.(sectionSelector).forEach(initSection);
  };

  if (window.sponsoringPartnershipsCarousel) {
    window.sponsoringPartnershipsCarousel.init(document);
    return;
  }

  window.sponsoringPartnershipsCarousel = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(sectionSelector)
      ? event.target
      : event.target.querySelector?.(sectionSelector);
    section?.sponsoringPartnershipsCleanup?.();
  });
})();
